/**
 * Hero ring data adapter (Phase 0).
 * Converts raw PFRA component scores from `useScoring().componentScores`
 * (each `number | null`, where `null` = exempt) into ring segments whose
 * `pct` is the component's achievement against its own maximum (0..100).
 */

export type ComponentKey = 'pu' | 'core' | 'cardio' | 'whtr'

/** Maximum raw points per component (from useScoring.calculate compMap). */
export const RING_MAX: Record<ComponentKey, number> = {
  pu: 15,
  core: 15,
  cardio: 50,
  whtr: 20,
}

/** Locked component colors (design spec §3). */
export const RING_META: Record<ComponentKey, { label: string; color: string }> = {
  pu: { label: 'Push-ups', color: '#3a8fcc' },
  core: { label: 'Core', color: '#2ecc71' },
  cardio: { label: 'Cardio', color: '#e67e22' },
  whtr: { label: 'WHtR', color: '#e74c3c' },
}

const ORDER: ComponentKey[] = ['pu', 'core', 'cardio', 'whtr']

export interface RingSegment {
  key: ComponentKey
  label: string
  color: string
  raw: number | null
  max: number
  /** 0..100, rounded to one decimal; `null` when the component is exempt. */
  pct: number | null
  exempt: boolean
}

export type RawComponentScores = Record<ComponentKey, number | null>

function toPct(raw: number, max: number): number {
  const clamped = Math.min(Math.max(raw, 0), max)
  return Math.round((clamped / max) * 1000) / 10
}

// ---------------------------------------------------------------------------
// Live composite + category (outer-ring "category color influence")
// ---------------------------------------------------------------------------

import { compositeToCategory } from '@/features/scoring/lib/scoringHelpers'
import type { Category } from '@/features/scoring/lib/types'

export type ScoreCategory = Category

const CATEGORY_RING_COLOR: Record<ScoreCategory, string> = {
  EXCELLENT: '#2ecc71',
  SATISFACTORY: '#a07840',
  UNSATISFACTORY: '#e74c3c',
}

/** Outer-ring influence color for a category; gold default when none. */
export function categoryRingColor(category: ScoreCategory | null | string): string {
  if (!category) return '#a07840'
  if (category === 'MARGINAL') return CATEGORY_RING_COLOR.UNSATISFACTORY
  return CATEGORY_RING_COLOR[category as ScoreCategory] ?? CATEGORY_RING_COLOR.UNSATISFACTORY
}

export interface ScoreSummary {
  /** 0..100 over the non-exempt components; `null` when every component is exempt. */
  composite: number | null
  category: ScoreCategory | null
}

/**
 * Live composite over the non-exempt components (exempt = `null`, excluded and
 * redistributed proportionally), matching useScoring.calculate. Thresholds:
 * ≥90 EXCELLENT, ≥75 SATISFACTORY, else UNSATISFACTORY.
 */
export function summarizeComponentScores(scores: RawComponentScores): ScoreSummary {
  let rawSum = 0
  let maxSum = 0
  for (const key of ORDER) {
    const raw = scores[key]
    if (raw === null) continue
    rawSum += raw
    maxSum += RING_MAX[key]
  }
  if (maxSum === 0) return { composite: null, category: null }

  const composite = (rawSum / maxSum) * 100
  return { composite, category: compositeToCategory(composite) }
}

export function toRingSegments(scores: RawComponentScores): RingSegment[] {
  return ORDER.map((key) => {
    const raw = scores[key]
    const max = RING_MAX[key]
    const exempt = raw === null
    return {
      key,
      label: RING_META[key].label,
      color: RING_META[key].color,
      raw,
      max,
      pct: exempt ? null : toPct(raw as number, max),
      exempt,
    }
  })
}
